import React, { useEffect, useMemo, useState } from "react";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import ToolShell from "./ToolShell";
import SavePrompt from "./SavePrompt";
import { Panel, TextField, Stat } from "./ToolUI";
import { saveResult, tierFor, loadDraft, saveDraft } from "./toolkit";

const PILLARS = [
  { key: "sort", name: "Sort", qs: [
    "Only items needed for current work are at the workstation",
    "A red-tag area exists and is cleared on a set schedule",
    "Aisles and walkways are free of obstructions",
    "No unneeded tools, fixtures or parts on benches or machines",
    "Obsolete documents and info are removed from boards",
  ] },
  { key: "set", name: "Set in Order", qs: [
    "Every tool has a labeled home (shadow board, outline)",
    "Floor markings and lanes are clear and intact",
    "Inventory locations show min/max levels",
    "Safety equipment is visible, accessible and labeled",
    "Any item can be found in under 30 seconds",
  ] },
  { key: "shine", name: "Shine", qs: [
    "Floors and machines are free of dirt, oil and debris",
    "A cleaning schedule is posted and followed",
    "Leaks and contamination sources are identified and tagged",
    "Cleaning supplies are stored in place and stocked",
    "Cleaning doubles as inspection — abnormalities get reported",
  ] },
  { key: "std", name: "Standardize", qs: [
    "Photos / visual standards of the ideal state are posted",
    "Standard work and SOPs are current and at point of use",
    "Color coding is consistent across the area",
    "5S responsibilities are assigned (map or roster)",
    "Abnormalities are visible at a glance",
  ] },
  { key: "sustain", name: "Sustain", qs: [
    "5S audits are performed on schedule",
    "Audit scores are trended and posted",
    "Actions from the previous audit are closed",
    "Operators are trained and can explain 5S in their area",
    "Leaders take part in regular gemba / 5S walks",
  ] },
];
const LABELS = ["Not started", "Poor", "Fair", "Good", "Excellent"];
const TOTAL_Q = 25;

export default function FiveSAudit() {
  const [meta, setMeta] = useState(() => loadDraft("fives_meta", { area: "", auditor: "", date: new Date().toISOString().slice(0, 10) }));
  const [ans, setAns] = useState(() => loadDraft("fives", {}));
  const answered = Object.keys(ans).length;
  const complete = answered === TOTAL_Q;

  useEffect(() => { saveDraft("fives", ans); saveDraft("fives_meta", meta); }, [ans, meta]);

  const pillarScores = useMemo(() => PILLARS.map((p) => ({
    pillar: p.name, key: p.key,
    score: p.qs.reduce((s, _, i) => s + (ans[`${p.key}-${i}`] ?? 0), 0),
  })), [ans]);
  const total = pillarScores.reduce((s, p) => s + p.score, 0);
  const tier = tierFor(total);
  const weakest = [...pillarScores].sort((a, b) => a.score - b.score)[0];

  useEffect(() => { if (complete) saveResult("fives", { score: total, pillars: pillarScores }); }, [complete, total, pillarScores]);

  const share = complete ? {
    slug: "5s-audit", tool: "5S Audit Score", scoreText: `${total}/100`, buttonLabel: "Share my 5S score",
    label: `${meta.area || "Our area"} · ${tier.label}`,
    bar: { value: total, target: 85, targetLabel: "World-class 85+" },
    tagline: "Building a world-class floor with RuleAI",
    caption: `Our ${meta.area || "area"} scored ${total}/100 on a 25-point 5S audit. Next target: 85+ with RuleAI.`,
  } : null;

  return (
    <ToolShell toolKey="fives" overline="Free tool · 5S" title="5S Audit Scorer"
      intro="Walk the area and score each of the 25 items from 0 (not started) to 4 (excellent). Your radar chart and weakest pillar update live. Progress saves in your browser."
      share={share} results={{ score: total, pillars: pillarScores, ...meta }}
      savePrompt={complete && <SavePrompt toolKey="fives" what="5S audit" />}>
      <Panel overline="01 / Audit details" testid="fives-meta">
        <div className="grid sm:grid-cols-3 gap-4">
          <TextField label="Area / cell" value={meta.area} onChange={(v) => setMeta({ ...meta, area: v })} placeholder="Line 3 — Assembly" testid="fives-area" />
          <TextField label="Auditor" value={meta.auditor} onChange={(v) => setMeta({ ...meta, auditor: v })} testid="fives-auditor" />
          <TextField type="date" label="Date" value={meta.date} onChange={(v) => setMeta({ ...meta, date: v })} testid="fives-date" />
        </div>
      </Panel>
      <div className="grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-6 items-start">
        <div className="space-y-6">
          {PILLARS.map((p, pi) => (
            <Panel key={p.key} overline={`S${pi + 1} · ${pillarScores[pi].score}/20`} title={p.name} testid={`fives-pillar-${p.key}`}>
              <div className="space-y-5">
                {p.qs.map((q, i) => {
                  const id = `${p.key}-${i}`;
                  return (
                    <div key={id} className="break-inside-avoid">
                      <div className="text-sm mb-2">{i + 1}. {q}</div>
                      <div className="grid grid-cols-5 gap-1.5">
                        {LABELS.map((l, v) => (
                          <button key={v} type="button" onClick={() => setAns({ ...ans, [id]: v })} title={l}
                            className={`h-10 rounded-sm border text-xs font-mono-plex transition-colors ${ans[id] === v ? "bg-primary border-primary text-white" : "border-border hover:border-primary text-muted-foreground"}`}
                            data-testid={`fives-q-${id}-${v}`}>
                            {v}<span className="hidden md:inline"> · {l}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>
          ))}
        </div>
        <div className="lg:sticky lg:top-20 space-y-4">
          <Panel overline="Live score" testid="fives-results">
            <Stat big label="5S Score" value={`${total}/100`} sub={complete ? tier.label : `${answered}/${TOTAL_Q} answered`} tone={complete ? tier.tone : ""} testid="fives-score" />
            <div className="h-64 mt-4">
              <ResponsiveContainer>
                <RadarChart data={pillarScores} outerRadius="72%">
                  <PolarGrid stroke="#3f3f46" />
                  <PolarAngleAxis dataKey="pillar" tick={{ fill: "#a1a1aa", fontSize: 11 }} />
                  <PolarRadiusAxis domain={[0, 20]} tick={false} axisLine={false} />
                  <Radar dataKey="score" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.35} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            {answered > 0 && <p className="text-sm text-muted-foreground" data-testid="fives-weakest">Weakest pillar: <span className="text-accent font-semibold">{weakest.pillar}</span> ({weakest.score}/20). Fix this first — gains here lift every other S.</p>}
            {answered > 0 && <button onClick={() => setAns({})} className="mt-3 text-xs font-mono-plex uppercase tracking-widest text-muted-foreground hover:text-foreground no-print" data-testid="fives-reset">Reset audit</button>}
          </Panel>
        </div>
      </div>
    </ToolShell>
  );
}
